import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Paper,
  SimpleGrid,
  Group,
  Stack,
  Table,
  Badge,
  Pagination,
  TextInput,
  Button,
  Select,
  Tooltip,
  Loader,
  Center,
  Modal,
  Divider,
  Alert,
  Tabs,
  Anchor,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconFileText,
  IconClock,
  IconCheck,
  IconSearch,
  IconSend,
  IconRefresh,
  IconFilter,
  IconAlertCircle,
  IconListDetails,
  IconBook,
  IconLock,
} from "@tabler/icons-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  useBillingStats,
  useBillingCases,
  useSubmitToTebra,
  useRetrySubmission,
  useBillingConfiguration,
} from "../../hooks/useBilling";
import {
  BillingStatCard,
  type BillingStatCardProps,
} from "./components/BillingStatCard";
import { RevenueStatCard } from "./components/RevenueStatCard";
import { isBillingAutomationAllowed } from "../../utils/manualTebraEntry";

interface BillingFilters {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

function billingStatusBadge(billingStatus: string): React.ReactNode {
  switch (billingStatus) {
    case "submitted":
      return (
        <Badge color="green" variant="light" size="sm">
          Submitted
        </Badge>
      );
    case "billed":
      return (
        <Badge color="green" variant="light" size="sm">
          Billed
        </Badge>
      );
    case "submitting":
      return (
        <Badge color="cyan" variant="light" size="sm">
          Submitting
        </Badge>
      );
    case "reconciliation_required":
      return (
        <Badge color="grape" variant="light" size="sm">
          Reconciliation Required
        </Badge>
      );
    case "error":
      return (
        <Badge color="red" variant="light" size="sm">
          Error
        </Badge>
      );
    case "rejected":
      return (
        <Badge color="red" variant="light" size="sm">
          Rejected
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge color="yellow" variant="light" size="sm">
          Pending
        </Badge>
      );
  }
}

const STATUS_OPTIONS = [
  { value: "successful", label: "Submitted / billed" },
  { value: "pending", label: "Pending" },
  { value: "submitting", label: "Submitting" },
  { value: "submitted", label: "Submitted" },
  { value: "billed", label: "Billed" },
  { value: "rejected", label: "Rejected" },
  { value: "error", label: "Error" },
  { value: "reconciliation_required", label: "Reconciliation required" },
];

const ITEMS_PER_PAGE = 20;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// 24 months of history is enough to look back across a full year boundary
// without overwhelming the dropdown.
const MONTH_OPTIONS_DEPTH = 24;

function buildMonthOptions(today: Date): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  for (let offset = 0; offset < MONTH_OPTIONS_DEPTH; offset += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    const year = d.getFullYear();
    const monthOneIndexed = d.getMonth() + 1;
    options.push({
      value: `${year}-${monthOneIndexed.toString().padStart(2, "0")}`,
      label: `${MONTH_NAMES[d.getMonth()]} ${year}`,
    });
  }
  return options;
}

function parseMonthValue(value: string): { year: number; month: number } {
  const [yearStr, monthStr] = value.split("-");
  return { year: parseInt(yearStr, 10), month: parseInt(monthStr, 10) };
}

function currentMonthValue(today: Date): string {
  return `${today.getFullYear()}-${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}`;
}

export default function BillingDashboardPage() {
  const navigate = useNavigate();
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<BillingFilters>({});
  const [pendingFilters, setPendingFilters] = useState<BillingFilters>({});
  const [page, setPage] = useState(1);

  // Month selector: the page boots fixed to "today" so the option list and
  // default value stay stable across renders (avoids the dropdown shifting
  // if the user keeps the page open across midnight).
  const today = useMemo(() => new Date(), []);
  const monthOptions = useMemo(() => buildMonthOptions(today), [today]);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    currentMonthValue(today)
  );
  const { year: selectedYear, month: selectedMonthIndex1 } = useMemo(
    () => parseMonthValue(selectedMonth),
    [selectedMonth]
  );

  const handleMonthChange = useCallback((value: string | null) => {
    if (!value) return;
    setSelectedMonth(value);
  }, []);

  // Tab state — persisted in the URL so the selected tab survives refresh
  // and back-navigation. We push (not replace) so each switch is a history
  // entry, per the requirement that tab changes show in navigation history.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "cases";

  const setActiveTab = useCallback(
    (next: string | null) => {
      if (!next) return;
      setSearchParams((prev) => {
        prev.set("tab", next);
        return prev;
      });
    },
    [setSearchParams]
  );

  // Build query params
  const queryParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status: filters.status || undefined,
      search: debouncedSearch || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    }),
    [page, filters, debouncedSearch]
  );

  const { data: stats, isLoading: isLoadingStats } = useBillingStats({
    year: selectedYear,
    month: selectedMonthIndex1,
  });
  const {
    data: casesData,
    isLoading: isLoadingCases,
    refetch,
  } = useBillingCases(queryParams);

  const submitMutation = useSubmitToTebra();
  const retryMutation = useRetrySubmission();
  const {
    data: billingConfiguration,
    isLoading: isLoadingBillingConfiguration,
    isError: isBillingConfigurationError,
    isFetching: isFetchingBillingConfiguration,
  } = useBillingConfiguration();
  const automationAllowed =
    !isLoadingBillingConfiguration &&
    !isBillingConfigurationError &&
    !isFetchingBillingConfiguration &&
    isBillingAutomationAllowed(billingConfiguration);

  const hasActiveFilters = useMemo(
    () => !!filters.status || !!filters.dateFrom || !!filters.dateTo,
    [filters.status, filters.dateFrom, filters.dateTo]
  );

  const handleOpenFilterModal = useCallback(() => {
    setPendingFilters({ ...filters });
    openFilterModal();
  }, [filters, openFilterModal]);

  const handleApplyFilters = useCallback(() => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  }, [pendingFilters, closeFilterModal]);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setSearch("");
    setPage(1);
  }, []);

  const handleSubmit = useCallback(
    (caseId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!automationAllowed) return;
      submitMutation.mutate(caseId);
    },
    [automationAllowed, submitMutation]
  );

  const handleRetry = useCallback(
    (caseId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!automationAllowed) return;
      retryMutation.mutate(caseId);
    },
    [automationAllowed, retryMutation]
  );

  const handleClickAwaiting = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setSearch("");
    setPage(1);
  }, []);

  const handleClickBilled = useCallback(() => {
    const startOfMonth = new Date(
      Date.UTC(selectedYear, selectedMonthIndex1 - 1, 1)
    );
    const endOfMonth = new Date(
      Date.UTC(selectedYear, selectedMonthIndex1, 1) - 1
    );
    const successfulMonthFilters = {
      status: "successful",
      dateFrom: startOfMonth.toISOString(),
      dateTo: endOfMonth.toISOString(),
    };
    setFilters(successfulMonthFilters);
    setPendingFilters(successfulMonthFilters);
    setSearch("");
    setPage(1);
  }, [selectedMonthIndex1, selectedYear]);

  const monthLabel = stats?.monthRange?.label ?? "";

  const awaitingTotal = stats?.awaitingBilling?.total ?? 0;
  const awaitingPending = stats?.awaitingBilling?.pending ?? 0;
  const awaitingError = stats?.awaitingBilling?.error ?? 0;
  const awaitingRejected = stats?.awaitingBilling?.rejected ?? 0;

  const statCards: BillingStatCardProps[] = [
    {
      title: "Cases Received",
      value: stats?.casesReceivedThisMonth ?? 0,
      description: monthLabel || "Selected month",
      icon: <IconFileText size={24} />,
      color: "blue",
      isLoading: isLoadingStats,
      tooltip:
        "Counts cases whose Date Received (the date the specimen was logged at the lab) falls within the selected month.",
    },
    {
      title: "Awaiting Billing",
      value: awaitingTotal,
      description: "Not yet synced or needing retry attention (current)",
      icon: <IconClock size={24} />,
      color: "yellow",
      isLoading: isLoadingStats,
      tooltip:
        "Signed-off cases with billing status of Pending, Error, or Rejected. This is always the current retry/attention snapshot and does not change with the selected month. Click to view in the table below.",
      subline: (
        <Text size="xs" c="dimmed">
          {awaitingPending} pending · {awaitingError} error · {awaitingRejected}{" "}
          rejected
        </Text>
      ),
      onClick: handleClickAwaiting,
    },
    {
      title: "Entered in Tebra",
      value: stats?.billedThisMonth ?? 0,
      description: monthLabel || "Manual and API entries",
      icon: <IconCheck size={24} />,
      color: "green",
      isLoading: isLoadingStats,
      tooltip:
        "Counts cases whose Tebra encounter was recorded during the selected month. Manual entries and API-created Draft encounters are tracked separately below; older rows without provenance are labeled legacy. Click to view them.",
      subline: (
        <Text size="xs" c="dimmed">
          {stats?.manualEnteredThisMonth ?? 0} manual ·{" "}
          {stats?.apiSubmittedThisMonth ?? 0} API
          {(stats?.unattributedSubmittedThisMonth ?? 0) > 0
            ? ` · ${stats?.unattributedSubmittedThisMonth} legacy`
            : ""}
        </Text>
      ),
      onClick: handleClickBilled,
    },
  ];

  const tableScopeLabel = useMemo(() => {
    if (filters.status === "successful") return "Submitted / billed";
    if (filters.status === "submitted") return "Submitted";
    if (filters.status === "billed") return "Billed";
    if (filters.status === "pending") return "Pending";
    if (filters.status === "error") return "Error";
    if (filters.status === "rejected") return "Rejected";
    return "Awaiting Billing";
  }, [filters.status]);

  const totalPages = casesData?.pagination?.totalPages ?? 1;

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <div>
              <Title order={2}>Billing Dashboard</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Overview of billing activity and cases
              </Text>
            </div>
            <Select
              label="Month"
              data={monthOptions}
              value={selectedMonth}
              onChange={handleMonthChange}
              allowDeselect={false}
              w={220}
            />
          </Group>

          {!automationAllowed && (
            <Alert
              icon={<IconLock size={17} />}
              color={
                isBillingConfigurationError ||
                billingConfiguration?.mode === "blocked"
                  ? "red"
                  : "blue"
              }
              title={
                isLoadingBillingConfiguration || isFetchingBillingConfiguration
                  ? "Checking Billing Mode"
                  : isBillingConfigurationError ||
                    billingConfiguration?.mode === "blocked"
                  ? "Billing Configuration Blocked"
                  : "Manual Billing Mode"
              }
            >
              <Text size="sm">
                {isLoadingBillingConfiguration || isFetchingBillingConfiguration
                  ? "Checking billing configuration. Automated submission is unavailable."
                  : isBillingConfigurationError
                  ? "Billing configuration could not be verified, so automated submission is safely disabled."
                  : billingConfiguration?.message ||
                    "Cases must be entered manually in Tebra during this testing period."}{" "}
                <Anchor
                  component={Link}
                  to="/reports/manual-tebra-entry"
                  fw={600}
                >
                  Open Manual Tebra Entry report
                </Anchor>
              </Text>
            </Alert>
          )}

          {/* Fees-not-configured banner: explains $0 revenue values. */}
          {!isLoadingStats && stats && !stats.feesConfigured && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="orange"
              variant="light"
              style={{ maxWidth: 650 }}
            >
              <Text size="sm">
                CPT-code fees have not been populated, so Recorded and Pending
                charge values will show as $0. Ask an administrator to set fees
                in the{" "}
                <Anchor component={Link} to="/admin/cpt-codes">
                  CPT Codes admin
                </Anchor>{" "}
                to enable revenue tracking.
              </Text>
            </Alert>
          )}

          {/* Stat Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {statCards.map((stat) => (
              <BillingStatCard key={stat.title} {...stat} />
            ))}
            <RevenueStatCard
              recorded={stats?.realizedRevenue ?? 0}
              pipeline={stats?.pipelineRevenue ?? 0}
              apiRecorded={stats?.apiSubmittedChargeValue ?? 0}
              manualRecorded={stats?.manualEnteredChargeValue ?? 0}
              unattributedRecorded={
                stats?.unattributedSubmittedChargeValue ?? 0
              }
              feesConfigured={stats?.feesConfigured ?? true}
              isLoading={isLoadingStats}
              monthLabel={monthLabel}
            />
          </SimpleGrid>

          <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab
                value="cases"
                leftSection={<IconListDetails size={16} />}
              >
                Cases
              </Tabs.Tab>
              <Tabs.Tab
                value="methodology"
                leftSection={<IconBook size={16} />}
              >
                How these numbers are calculated
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="cases" pt="md">
              <Stack gap="lg">
                {/* Search and Filters Row */}
                <Group align="stretch" grow>
                  {/* Search Paper */}
                  <Paper p="md" withBorder style={{ flex: 2 }}>
                    <Stack gap="sm">
                      <Group>
                        <IconSearch size={20} />
                        <Text fw={500}>Search</Text>
                      </Group>
                      <TextInput
                        placeholder="Search by case number or patient name..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        leftSection={<IconSearch size={16} />}
                      />
                    </Stack>
                  </Paper>

                  {/* Filters Paper */}
                  <Paper p="md" withBorder style={{ flex: 1 }}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group>
                          <IconFilter size={20} />
                          <Text fw={500}>Filters</Text>
                        </Group>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => refetch()}
                          loading={isLoadingCases}
                          leftSection={<IconRefresh size={16} />}
                        />
                      </Group>
                      <Button
                        variant="light"
                        onClick={handleOpenFilterModal}
                        fullWidth
                      >
                        {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                      </Button>
                      {hasActiveFilters && (
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" c="dimmed">
                            Active:
                          </Text>
                          {filters.status && (
                            <Badge variant="light" color="blue">
                              Status:{" "}
                              {filters.status.charAt(0).toUpperCase() +
                                filters.status.slice(1)}
                            </Badge>
                          )}
                          {filters.dateFrom && (
                            <Badge variant="light" color="grape">
                              From: {filters.dateFrom}
                            </Badge>
                          )}
                          {filters.dateTo && (
                            <Badge variant="light" color="grape">
                              To: {filters.dateTo}
                            </Badge>
                          )}
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            color="gray"
                            onClick={handleClearAllFilters}
                          >
                            Clear All
                          </Button>
                        </Group>
                      )}
                      <Text size="sm" c="dimmed">
                        {casesData?.pagination?.total ?? 0} case
                        {(casesData?.pagination?.total ?? 0) !== 1
                          ? "s"
                          : ""}{" "}
                        found
                      </Text>
                    </Stack>
                  </Paper>
                </Group>

                {/* Table Section */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    {/* Table Header with Pagination */}
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        {tableScopeLabel} ({casesData?.pagination?.total ?? 0})
                      </Text>
                      {totalPages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                            {Math.min(
                              page * ITEMS_PER_PAGE,
                              casesData?.pagination?.total ?? 0
                            )}{" "}
                            of {casesData?.pagination?.total ?? 0}
                          </Text>
                          <Pagination
                            total={totalPages}
                            value={page}
                            onChange={setPage}
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {/* Table */}
                    {isLoadingCases ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : casesData?.data && casesData.data.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Case #</Table.Th>
                            <Table.Th>Patient Name</Table.Th>
                            <Table.Th>Physician</Table.Th>
                            <Table.Th>Insurance</Table.Th>
                            <Table.Th>Billing Scenario</Table.Th>
                            <Table.Th>Sign-Off Date</Table.Th>
                            <Table.Th style={{ width: 100 }}>Status</Table.Th>
                            <Table.Th style={{ width: 140 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {casesData.data.map((row) => (
                            <Table.Tr
                              key={row.id}
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/cases/${row.id}`)}
                            >
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {row.caseNumber}
                                </Text>
                              </Table.Td>
                              <Table.Td>{row.patientName || "—"}</Table.Td>
                              <Table.Td>{row.physicianName || "—"}</Table.Td>
                              <Table.Td>
                                {row.insuranceProvider || "—"}
                              </Table.Td>
                              <Table.Td>{row.billingScenario || "—"}</Table.Td>
                              <Table.Td>
                                {row.signOffDate
                                  ? new Date(
                                      row.signOffDate
                                    ).toLocaleDateString()
                                  : "—"}
                              </Table.Td>
                              <Table.Td>
                                <Group gap={4}>
                                  {billingStatusBadge(row.billingStatus)}
                                  {row.billingSubmissionMethod &&
                                    (row.billingStatus === "submitted" ||
                                      row.billingStatus === "billed") && (
                                      <Badge
                                        size="xs"
                                        variant="dot"
                                        color={
                                          row.billingSubmissionMethod ===
                                          "manual"
                                            ? "blue"
                                            : "grape"
                                        }
                                      >
                                        {row.billingSubmissionMethod ===
                                        "manual"
                                          ? "Manual"
                                          : "API"}
                                      </Badge>
                                    )}
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs">
                                  {automationAllowed &&
                                    row.billingStatus === "pending" && (
                                      <Tooltip label="Submit to Tebra">
                                        <Button
                                          variant="light"
                                          color="blue"
                                          size="compact-xs"
                                          loading={
                                            submitMutation.isPending &&
                                            submitMutation.variables === row.id
                                          }
                                          onClick={(e) =>
                                            handleSubmit(row.id, e)
                                          }
                                          leftSection={<IconSend size={14} />}
                                        >
                                          Submit
                                        </Button>
                                      </Tooltip>
                                    )}
                                  {automationAllowed &&
                                    (row.billingStatus === "error" ||
                                      row.billingStatus === "rejected") && (
                                      <Tooltip
                                        label={
                                          row.billingErrorMessage ||
                                          row.billingRejectionReason ||
                                          "Retry submission"
                                        }
                                        multiline
                                        w={300}
                                      >
                                        <Button
                                          variant="light"
                                          color="orange"
                                          size="compact-xs"
                                          loading={
                                            retryMutation.isPending &&
                                            retryMutation.variables === row.id
                                          }
                                          onClick={(e) =>
                                            handleRetry(row.id, e)
                                          }
                                          leftSection={
                                            <IconRefresh size={14} />
                                          }
                                        >
                                          Retry
                                        </Button>
                                      </Tooltip>
                                    )}
                                  {(row.billingStatus === "submitted" ||
                                    row.billingStatus === "billed") && (
                                    <Text size="xs" c="dimmed">
                                      ✓ Submitted
                                    </Text>
                                  )}
                                  {!automationAllowed &&
                                    [
                                      "pending",
                                      "error",
                                      "rejected",
                                      "reconciliation_required",
                                    ].includes(row.billingStatus) && (
                                      <Button
                                        size="compact-xs"
                                        variant="light"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          navigate(
                                            `/reports/manual-tebra-entry?search=${encodeURIComponent(
                                              row.caseNumber
                                            )}`
                                          );
                                        }}
                                      >
                                        Worksheet
                                      </Button>
                                    )}
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <Text c="dimmed" ta="center" py="xl">
                        {hasActiveFilters || debouncedSearch
                          ? "No cases match the current filters"
                          : `No ${tableScopeLabel.toLowerCase()} cases.`}
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="methodology" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Title order={4} style={{ maxWidth: 650 }}>
                    How these numbers are calculated
                  </Title>
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Cases Received This Month
                    </Text>
                    <Text size="sm" c="dimmed">
                      Cases whose Date Received falls within the current
                      calendar month
                      {monthLabel ? ` (${monthLabel})` : ""}. Resets on the 1st.
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Awaiting Billing
                    </Text>
                    <Text size="sm" c="dimmed">
                      Signed-off cases with billing status of Pending (ready to
                      sync), Error (failed attempt), or Rejected (requires
                      correction and retry). This number matches the row count
                      in the Cases tab when no extra filters are applied.
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Entered in Tebra This Month
                    </Text>
                    <Text size="sm" c="dimmed">
                      Cases whose Tebra encounter was recorded during the
                      selected calendar month. Manual billing-team entries and
                      API-created Draft encounters are tracked separately. This
                      does not mean a claim was sent or a payment was received.
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Recorded charge value
                    </Text>
                    <Text size="sm" c="dimmed">
                      Sum of configured CPT-code fees on cases whose Tebra
                      encounter was recorded in the selected month. Manual and
                      API values are shown separately. This is a charge-value
                      estimate, not realized revenue or payment.
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Pending charge value
                    </Text>
                    <Text size="sm" c="dimmed">
                      Sum of configured CPT-code fees on signed-off cases in
                      Pending, Error, or Rejected status. This is the current
                      retry/attention backlog, not a month-bound revenue figure.
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ maxWidth: 650 }}>
                    <Text size="sm" fw={500}>
                      Where do CPT-code fees come from?
                    </Text>
                    <Text size="sm" c="dimmed">
                      Fees are stored on each CPT code in the CPT Codes admin
                      (Admin → Medical Reference → CPT Codes). If a CPT code has
                      no fee set, it contributes $0 to both Submitted and
                      Pending charge values. When no CPT codes have fees, both
                      figures show $0 and a notice appears at the top of this
                      dashboard.
                    </Text>
                  </div>
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Box>

      {/* Filter Modal */}
      <Modal
        opened={filterModalOpened}
        onClose={closeFilterModal}
        title="Filter Billing Cases"
        size="sm"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Select
            label="Billing Status"
            placeholder="All attention statuses"
            data={STATUS_OPTIONS}
            value={pendingFilters.status || null}
            onChange={(value) =>
              setPendingFilters({
                ...pendingFilters,
                status: value || undefined,
              })
            }
            clearable
          />
          <DatePickerInput
            label={
              pendingFilters.status === "successful"
                ? "Billing Activity Date From"
                : "Sign-Off Date From"
            }
            placeholder="Select start date"
            value={
              pendingFilters.dateFrom ? new Date(pendingFilters.dateFrom) : null
            }
            onChange={(value: any) =>
              setPendingFilters({
                ...pendingFilters,
                dateFrom:
                  value instanceof Date
                    ? value.toISOString().split("T")[0]
                    : undefined,
              })
            }
            clearable
          />
          <DatePickerInput
            label={
              pendingFilters.status === "successful"
                ? "Billing Activity Date To"
                : "Sign-Off Date To"
            }
            placeholder="Select end date"
            value={
              pendingFilters.dateTo ? new Date(pendingFilters.dateTo) : null
            }
            onChange={(value: any) =>
              setPendingFilters({
                ...pendingFilters,
                dateTo:
                  value instanceof Date
                    ? value.toISOString().split("T")[0]
                    : undefined,
              })
            }
            clearable
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setPendingFilters({})}>
              Clear All
            </Button>
            <Button onClick={handleApplyFilters}>Apply Filters</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
